// Import react elements
import { useEffect, useState } from "react";

// Import components
import CreateButton from "global/components/CreateButton";
import { ImageLoader } from "global/components/ImageLoader";
import TwoColumnList from "global/components/TwoColumnList";
import DropdownSelect from "global/components/DropdownSelect";
import { CloseIcon } from "global/strich_icons/StrichIcons";



// Render MOCKUP pop up - show larger display image with editable compatibility settings and delete ability
const RenderMockupPopup = ({ isOpen, onClose, mockup, productCode, getMockupCloneTargets, handleCloneMockup, handleUpdateMockup, handleDeleteMockup, handleOpenCustomizrClick, isCustomizrReady }) => {

    // Store editable mockup view settings
    const [viewSettings, setViewSettings] = useState({
        aspects: [],
        rotations: []
    });

    // Store the loaded settings so changes can be detected
    const [savedViewSettings, setSavedViewSettings] = useState({
        aspects: [],
        rotations: []
    });

    // Store the available clone destinations for this product family
    const [cloneTargets, setCloneTargets] = useState([]);
    const [selectedCloneTarget, setSelectedCloneTarget] = useState("");
    const [cloning, setCloning] = useState(false);

    // Sync the form when the selected mockup changes
    useEffect(() => {

        // Return early if there is no mockup
        if (!mockup) { return; }

        // Read the supported aspects
        let aspects = [];
        if (Array.isArray(mockup?.view_settings?.aspects)) { aspects = [...mockup.view_settings.aspects]; }

        // Read the supported rotations
        let rotations = [];
        if (Array.isArray(mockup?.view_settings?.rotations)) { rotations = mockup.view_settings.rotations.map((rotation) => Number(rotation)); }

        // Set the editable view settings
        setViewSettings({
            aspects: aspects,
            rotations: rotations
        });

        // Store the loaded settings for change detection
        setSavedViewSettings({
            aspects: [...aspects],
            rotations: [...rotations]
        });

    }, [mockup]);

    // Sort the current settings before comparing them
    const currentAspects = [...viewSettings.aspects].sort();
    const currentRotations = [...viewSettings.rotations].sort((a, b) => a - b);

    // Sort the loaded settings before comparing them
    const savedAspects = [...savedViewSettings.aspects].sort();
    const savedRotations = [...savedViewSettings.rotations].sort((a, b) => a - b);

    // Check whether the mockup settings have actually changed
    const mockupHasUnsavedChanges =
        currentAspects.join("|") !== savedAspects.join("|") ||
        currentRotations.join("|") !== savedRotations.join("|");

    // Load valid sibling / child clone destinations for this custom product
    useEffect(() => {

        let isCurrent = true;

        // Reset the clone controls while the selected mockup changes
        setCloneTargets([]);
        setSelectedCloneTarget("");

        if (!isOpen || !mockup || !productCode || !getMockupCloneTargets) { return () => { isCurrent = false; }; }

        const loadCloneTargets = async () => {

            const response = await getMockupCloneTargets(productCode, mockup.id);
            if (!isCurrent || !response?.success) { return; }

            const targets = Array.isArray(response.results?.targets) ? response.results.targets : [];

            setCloneTargets(targets);
            setSelectedCloneTarget(targets.length > 0 ? targets[0].product_code : "");

        };

        loadCloneTargets();

        return () => { isCurrent = false; };

    }, [isOpen, mockup, productCode, getMockupCloneTargets]);



    // Clone to one selected family product
    const handleCloneToTarget = async () => {

        if (!selectedCloneTarget || cloning) { return; }

        setCloning(true);
        const response = await handleCloneMockup(mockup, selectedCloneTarget, "N");
        setCloning(false);

        if (response?.success) { onClose?.(); }

    };


    // Clone this mockup to every available family target
    const handleCloneToAll = async () => {

        if (cloneTargets.length === 0 || cloning) { return; }

        setCloning(true);

        let allSuccessful = true;

        for (const target of cloneTargets) {

            const response = await handleCloneMockup(mockup, target.product_code, "N");

            if (!response?.success) {
                allSuccessful = false;
                break;
            }

        }

        setCloning(false);

        if (allSuccessful) { onClose?.(); }

    };

    // Toggle one supported view setting
    const handleSettingToggle = (settingName, settingValue) => {

        // Update the selected setting list
        setViewSettings((prev) => {

            // Read the current values
            const currentValues = prev[settingName];

            // Check whether this value is already selected
            const valueIsSelected = currentValues.includes(settingValue);

            // Keep at least one value selected in each setting group
            if (valueIsSelected && currentValues.length === 1) { return prev; }

            // Start with the current value list
            let updatedValues = [...currentValues];

            // Remove the selected value
            if (valueIsSelected) {
                updatedValues = updatedValues.filter((currentValue) => currentValue !== settingValue);
            } else {

                // Add the selected value
                updatedValues.push(settingValue);
            }

            // Return the updated settings
            return {
                ...prev,
                [settingName]: updatedValues
            };

        });

    };



    // Render one selectable view setting button
    const renderSettingButton = (settingName, settingValue, label) => {

        // Check whether this setting is selected
        const isSelected = viewSettings[settingName].includes(settingValue);

        // Build the button class
        let buttonClassName = "selectable__chip";
        if (isSelected) { buttonClassName += " selectable__chip--selected"; }

        // Render the option
        return (
            <button type="button" className={buttonClassName} aria-pressed={isSelected} onClick={() => handleSettingToggle(settingName, settingValue)}>
                {label}
            </button>
        );

    };



    // Handle save button click
    const handleSave = () => {

        // Save the mockup view settings
        handleUpdateMockup(mockup, viewSettings);

    };


    // If isOpen is false return early (don't show the pop up)
    if (!isOpen) return null;


    return (

        <div className="popup-overlay">
        
            <div className="popup-container popup-container--small mockup-popup">

                {/* This is the modal header */}
                <div className="popup__header">

                    {/* This is the title area */}
                    <div className="popup__headerLeft">

                        {/* This is the parent title */}
                        <h2 className="popup__title">

                            Mockup

                        </h2>

                        {/* This is the parent subtitle */}
                        <p className="popup__subtitle">
                            {mockup?.id}
                        </p>

                    </div>

                    {/* This is the header actions */}
                    <div className="popup__headerRight">

                        {/* This is the close button */}
                        <button type="button" className="popup__close" onClick={() => onClose?.()} title="Close">
                            <CloseIcon />
                        </button>

                    </div>

                </div>


                <div className="popup-content popup-content__body mockup-popup__body">

                    <div className="view-popup_container mockup-popup__image-area">

                        <div className="view-popup_image-container mockup-popup__image-container">
                            
                            <ImageLoader src={mockup.mockup_image_url} alt="Mockup" className="surface-details_image" wrapperCSS="NA" imageLoaderImageCSS="view_bg-image-NA" />

                        </div>

                    </div>

                    {cloneTargets.length > 0 &&

                        <TwoColumnList
                            property={<label>Clone to</label>}
                            value={
                                <div className="mockup-clone-controls">
                                    <DropdownSelect
                                        name="mockup_clone_target"
                                        value={selectedCloneTarget}
                                        onChange={(event) => setSelectedCloneTarget(event.target.value)}
                                        options={cloneTargets.map((target) => ({
                                            value: target.product_code,
                                            name: (target.sku ? target.sku + " - " : "") + target.product_code
                                        }))}
                                        disabled={cloning}
                                    />

                                    <CreateButton
                                        title={cloning ? "Cloning..." : "Clone"}
                                        tooltip="Clone Mockup"
                                        size="s"
                                        onClick={handleCloneToTarget}
                                        disabled={cloning || !selectedCloneTarget}
                                    />

                                    <CreateButton
                                        title={cloning ? "Cloning..." : "Clone To All"}
                                        tooltip="Clone Mockup to All"
                                        size="s"
                                        onClick={handleCloneToAll}
                                        disabled={cloning}
                                    />
                                </div>
                            }
                        />

                    }

                </div>

                <br />

                <div className="tabs-footer tabs-footer--fill-no-background mockup-popup__footer">

                    {/* Keep destructive actions on the left */}
                    <div className="mockup-popup__footer-left">

                        <CreateButton title="Delete" isDelete={true} tooltip="Delete Mockup" size="m" icon="/images/icon_bin.png" onClick={() => handleDeleteMockup(mockup)} />

                    </div>

                    {/* Keep view compatibility controls in the centre */}
                    <div className="mockup-popup__footer-settings">

                        <div className="mockup-popup__footer-setting">

                            <span className="mockup-popup__footer-label">Aspects</span>

                            <div className="selectable__chips">
                                {renderSettingButton("aspects", "S", "Square")}
                                {renderSettingButton("aspects", "T", "Tall")}
                                {renderSettingButton("aspects", "W", "Wide")}
                            </div>

                        </div>

                        <div className="mockup-popup__footer-setting">

                            <span className="mockup-popup__footer-label">Rotations</span>

                            <div className="selectable__chips">
                                {renderSettingButton("rotations", 0, "0")}
                                {renderSettingButton("rotations", 90, "90")}
                                {renderSettingButton("rotations", 180, "180")}
                                {renderSettingButton("rotations", 270, "270")}
                            </div>

                        </div>

                    </div>

                    {/* Keep normal actions on the right */}
                    <div className="mockup-popup__footer-right">

                        <CreateButton
                            title={isCustomizrReady ? "Edit in Customizr" : "Loading Customizr..."}
                            tooltip="Edit Mockup"
                            size="m"
                            onClick={() => {
                                onClose?.();
                                handleOpenCustomizrClick("mockup", mockup.id);
                            }}
                            disabled={!isCustomizrReady}
                        />

                        <CreateButton
                            title="Save"
                            tooltip="Save Mockup"
                            size="m"
                            onClick={handleSave}
                            disabled={!mockupHasUnsavedChanges}
                        />

                    </div>

                </div>

            </div>

        </div>
    )

}; export default RenderMockupPopup;